/**
 * @fileoverview Shared scoring-grid cell resolution and Query Builder evaluation.
 * @module system-web/services/legacyMigrationGridFilter
 *
 * Responsibilities:
 * - Resolve a displayed cell value from a scored migration row.
 * - Evaluate Filters / Query Builder conditions against that resolved value.
 *
 * Dependencies:
 * - legacyMigrationService ScoredGridRow
 *
 * Architecture:
 * - Pure helpers used by LegacyMigrationGrid (client) so Filters/Query match visible cells.
 * - Server applies the same JSON query + filter.* params before pagination.
 *
 * Notes:
 * - Client-side filtering is only a fallback when the server has not yet re-fetched.
 */
import type { ScoredGridRow } from './legacyMigrationService';

export type QueryOperator =
  | 'contains' | 'notContains' | 'equals' | 'notEquals'
  | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty';

export interface QueryCondition {
  id: string;
  field: string;
  operator: QueryOperator;
  value: string;
}

export interface QueryGroup {
  id: string;
  logic: 'AND' | 'OR';
  conditions: (QueryCondition | QueryGroup)[];
}

/**
 * @contract LegacyGridQueryGroup
 * Purpose: Query Builder payload sent as `query` on the live grid fetch.
 * Fields:  id, logic, conditions (nested groups or field/operator/value).
 * Rules:   Empty conditions array matches all rows.
 * Sync:    Online-exception request param only; never persisted in Dexie.
 */
export function isQueryGroup(item: QueryCondition | QueryGroup): item is QueryGroup {
  return Boolean(item && typeof item === 'object' && 'logic' in item && 'conditions' in item);
}

export function getNestedValue(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const parts = path.replace(/\[(\w+)\]/g, '.$1').split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
}

/**
 * @reusable
 * Usage: Resolve the string shown for a grid column, including aliased occupancy/chemical fields.
 * Rules: presentation-only; never invents data.
 */
export function getResolvedCellValue(r: ScoredGridRow, key: string): string {
  if (key === 'score') return String(r.score ?? '');
  if (key === 'band') return String(r.band ?? '');
  if (key === 'legacyId' || key === 'id') return String(r.legacyId ?? '');

  let val = r.cells[key];

  if (val == null && (key.includes('.') || key.includes('['))) {
    val = getNestedValue(r.cells, key);
  }

  if (val == null) {
    const k = key.toLowerCase();
    if (k.includes('insp') || k.includes('frusa_insp')) {
      val = r.cells.frusa_insp_id || r.cells.f_frusa_insp_id || r.cells.inspectionId || r.cells.inspection_id || r.cells.insp_id;
    } else if (k.includes('stru') || k.includes('buildingtype')) {
      val = r.cells.stru_type_name || r.cells.buildingTypeName || r.cells.building_type || r.cells.stru_type;
    } else if (k.includes('address')) {
      val = r.cells.fullAddress || r.cells.address || r.cells.street || (r.cells.number && r.cells.street ? `${r.cells.number} ${r.cells.street}` : null);
    } else if (k.includes('category')) {
      val = r.cells.categoryLabel || r.cells.category || r.cells.OccCategoryId || r.cells.category_name;
    } else if (k === 'name' || k.includes('chem')) {
      val = r.cells.chemical_name || r.cells.chemicalname || r.cells.chem_name || r.cells.name || r.cells.chemicalName;
    } else if (k.includes('cas')) {
      val = r.cells.cas_number || r.cells.cas || r.cells.casNumber;
    } else if (k.includes('haz') || k.includes('class')) {
      val = r.cells.hazard_class || r.cells.hazcat || r.cells.hazardClass;
    } else if (k.includes('un_') || k.includes('unnum')) {
      val = r.cells.un_number || r.cells.unNumber || r.cells.un_no;
    } else if (k.includes('unit')) {
      val = r.cells.unit_of_measure || r.cells.unit || r.cells.unit_measure;
    } else if (k.includes('place') || k.includes('position')) {
      val = r.cells.placement || r.cells.position || r.cells.location;
    } else if (k.includes('note') || k.includes('comment')) {
      val = r.cells.notes || r.cells.comments || r.cells.descript || r.cells.description;
    } else if (k.includes('tank') || k.includes('reg')) {
      val = r.cells.tank_number || r.cells.tank_reg || r.cells.tank_id || r.cells.descript;
    } else if (k.includes('permit')) {
      val = r.cells.permit_no || r.cells.permitNumber || r.cells.type;
    }
  }

  if (val == null) {
    const targetKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [ckey, cval] of Object.entries(r.cells || {})) {
      if (ckey.toLowerCase().replace(/[^a-z0-9]/g, '') === targetKey) {
        val = cval;
        break;
      }
    }
  }

  if (val == null) return '';

  if (typeof val === 'object') {
    const obj = val as { name?: unknown; code?: unknown; description?: unknown };
    if (obj.name != null) return String(obj.name);
    if (obj.code != null) return String(obj.code);
    if (obj.description != null) return String(obj.description);
    return JSON.stringify(val);
  }

  return String(val);
}

function evaluateCondition(row: ScoredGridRow, cond: QueryCondition): boolean {
  const strVal = getResolvedCellValue(row, cond.field).toLowerCase();
  const q = (cond.value || '').toLowerCase();
  switch (cond.operator) {
    case 'contains': return strVal.includes(q);
    case 'notContains': return !strVal.includes(q);
    case 'equals': return strVal === q;
    case 'notEquals': return strVal !== q;
    case 'startsWith': return strVal.startsWith(q);
    case 'endsWith': return strVal.endsWith(q);
    case 'isEmpty': return strVal.trim() === '';
    case 'isNotEmpty': return strVal.trim() !== '';
    default: return true;
  }
}

/**
 * @reusable
 * Usage: Apply Query Builder groups (AND/OR, nested).
 * Rules: Empty group matches all rows.
 */
export function evaluateQueryGroup(row: ScoredGridRow, group: QueryGroup): boolean {
  if (!group?.conditions?.length) return true;
  const results = group.conditions.map((item) => (
    isQueryGroup(item) ? evaluateQueryGroup(row, item) : evaluateCondition(row, item)
  ));
  return group.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

/**
 * @reusable
 * Usage: Search box — matches id, score, band, and every cell.
 * Rules: Case-insensitive substring.
 */
export function rowMatchesSearch(row: ScoredGridRow, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  if (String(row.legacyId || '').toLowerCase().includes(q)) return true;
  if (String(row.score ?? '').toLowerCase().includes(q)) return true;
  if (String(row.band || '').toLowerCase().includes(q)) return true;
  return Object.keys(row.cells || {}).some((k) => getResolvedCellValue(row, k).toLowerCase().includes(q));
}

/**
 * @reusable
 * Usage: Column Filters row plus Query Builder, applied before pagination.
 * Rules: Empty filters / empty query are no-ops.
 */
export function applyLegacyGridFilters(
  rows: ScoredGridRow[],
  opts: {
    search?: string;
    columnFilters?: Record<string, string>;
    query?: QueryGroup | null;
  },
): ScoredGridRow[] {
  let result = rows;
  if (opts.search?.trim()) {
    result = result.filter((r) => rowMatchesSearch(r, opts.search as string));
  }
  for (const [key, val] of Object.entries(opts.columnFilters ?? {})) {
    if (!val?.trim()) continue;
    const q = val.trim().toLowerCase();
    result = result.filter((r) => getResolvedCellValue(r, key).toLowerCase().includes(q));
  }
  if (opts.query && opts.query.conditions.length > 0) {
    result = result.filter((r) => evaluateQueryGroup(r, opts.query as QueryGroup));
  }
  return result;
}
